import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BASE_URL from '../../url';
import { X } from 'lucide-react';
import '../styles/PodModals.css';

const EditPodModal = ({ isOpen, onClose, onPodUpdated, podData }) => {
  const [teamName, setTeamName] = useState('');
  const [regionId, setRegionId] = useState('1');
  const [isBenchPod, setIsBenchPod] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const colors = [
    'linear-gradient(135deg, #3b82f6, #2563eb)', // Blue
    'linear-gradient(135deg, #8b5cf6, #6366f1)', // Purple
    'linear-gradient(135deg, #10b981, #059669)', // Green
    'linear-gradient(135deg, #f97316, #ea580c)', // Orange
    'linear-gradient(135deg, #ef4444, #dc2626)', // Red
    'linear-gradient(135deg, #ec4899, #db2777)', // Pink
    'linear-gradient(135deg, #0ea5e9, #2563eb)', // Teal
    'linear-gradient(135deg, #84cc16, #65a30d)', // Light Green
  ];
  const [selectedColor, setSelectedColor] = useState(colors[0]);

  const getSolidColor = (colorStr) => {
    if (colorStr && colorStr.includes('linear-gradient')) {
      const match = colorStr.match(/#([a-fA-F0-9]{6}|[a-fA-F0-9]{3})/);
      return match ? match[0] : '#3b82f6';
    }
    return colorStr;
  };

  useEffect(() => {
    if (isOpen && podData) {
      setTeamName(podData.teamName || '');
      setRegionId(podData.regionId || '1');
      setSelectedColor(podData.colorTheme || colors[0]);
      setIsBenchPod(podData.teamType === 'BENCH' || (podData.teamName && podData.teamName.toLowerCase().includes('bench')));
      setError('');
    }
  }, [isOpen, podData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!teamName) {
      setError('Please enter a team name.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      
      const updatePayload = {
        teamName,
        regionId: parseInt(regionId, 10),
        color: getSolidColor(selectedColor),
        teamType: isBenchPod ? 'BENCH' : 'GENERAL'
      };

      try {
        await axios.put(`${BASE_URL}/api/teams/${Math.abs(podData.teamId)}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (putErr) {
        if (putErr.response && putErr.response.status === 404) {
          await axios.put(`${BASE_URL}/api/recruitment/teams/${Math.abs(podData.teamId)}`, updatePayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          throw putErr;
        }
      }

      onPodUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating pod:', err);
      setError(err.response?.data?.message || 'Failed to update pod');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pod-modal-overlay">
      <div className="pod-modal-content">
        <div className="pod-modal-header blue">
          <h3 className="pod-modal-title">Edit Pod</h3>
          <span className="pod-modal-close" onClick={onClose}><X size={20} /></span>
        </div>
        
        <div className="pod-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="pod-modal-form-group">
              <label className="pod-modal-label">Pod Name <span className="required">*</span></label>
              <input 
                type="text" 
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder="e.g. Alpha Squad"
                className="pod-modal-input"
              />
            </div>

            <div className="pod-modal-form-group">
              <label className="pod-modal-label">Pod Color</label>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
                {colors.map((color) => (
                  <div 
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      background: color,
                      cursor: 'pointer',
                      boxShadow: selectedColor === color ? `0 0 0 2px white, 0 0 0 4px ${getSolidColor(color)}` : 'none',
                      transition: 'all 0.2s ease',
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}
                  />
                ))}
              </div>
            </div>

            <div className="pod-modal-form-group">
              <label className="pod-modal-label">Pod Type</label>
              <div className="pod-modal-btn-group">
                <button
                  type="button"
                  onClick={() => setRegionId('1')}
                  className={`pod-modal-toggle-btn ${regionId === '1' ? 'active' : 'inactive'}`}
                >
                  Onsite
                </button>
                <button
                  type="button"
                  onClick={() => setRegionId('2')}
                  className={`pod-modal-toggle-btn ${regionId === '2' ? 'active' : 'inactive'}`}
                >
                  Offshore
                </button>
              </div>
            </div>

            {/* Bench Designation Checkbox */}
            <div className="pod-modal-form-group" style={{ marginTop: '12px', background: '#fff7ed', padding: '10px 14px', borderRadius: '8px', border: '1px solid #fed7aa' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', margin: 0 }}>
                <input
                  type="checkbox"
                  checked={isBenchPod}
                  onChange={(e) => setIsBenchPod(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: '#ea580c', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '12.5px', fontWeight: 700, color: '#c2410c' }}>
                  Bench Sales Pod
                </span>
              </label>
              <p style={{ margin: '4px 0 0 24px', fontSize: '11px', color: '#9a3412' }}>
                Members of this pod will automatically be tracked as bench recruiters and tagged with the Bench badge.
              </p>
            </div>

            {error && <div className="pod-modal-error">{error}</div>}

            <div className="pod-modal-footer">
              <button type="button" onClick={onClose} className="pod-modal-btn-cancel">Cancel</button>
              <button type="submit" disabled={loading} className="pod-modal-btn-submit">
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditPodModal;
